import React from "react";
import { Fragment } from "react";
import { Listbox, Transition } from "@headlessui/react";
import { ArrowDownIcon } from "@/icons/primary";
import classNames from "classnames";
import { Cancel2Icon } from "@/icons/primary-icons-new";

export type CaSelectSize = "small" | "medium" | "large";

const BASE_CLASSES = ["font-sans-serif", "font-normal", "border", "rounded-sm", "w-full"];

const SIZE_CLASSES = {
  small: ["text-sm", "py-1", "px-3"],
  medium: ["text-sm", "py-3.5", "px-5"],
  large: ["text-lg", "py-5", "px-5"],
};

type SelectType = { label: string; value: any; [x: string]: any };

export type CaSelectProps = {
  size?: CaSelectSize;
  label?: string;
  value?: SelectType["value"];
  options: SelectType[];
  onChange?: (value: SelectType["value"]) => void;
  ["aria-invalid"]?: boolean;
  under?: React.ReactNode;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  onReset?: () => void;
};

function CaSelect({
  size,
  label,
  value,
  options,
  onChange,
  under,
  placeholder = "Select",
  className,
  disabled,
  onReset,
  ...props
}: CaSelectProps) {
  const prepareValue = options.find((item) => item.value === value);

  return (
    <Listbox
      value={prepareValue ? prepareValue : { label: "", value: "" }}
      onChange={(option) => onChange && onChange(option.value)}
      disabled={disabled}
    >
      {({ open, value: valueSelect }) => (
        <>
          {label && <div>{label}</div>}
          <div className={classNames("relative", className)}>
            <Listbox.Button
              className={classNames(
                BASE_CLASSES,
                SIZE_CLASSES[size || "medium"],
                props["aria-invalid"] ? "border-danger" : "border-mid-neutral-3",
                disabled
                  ? "bg-light-neutral-3 cursor-not-allowed"
                  : "bg-white hover:border-mid-neutral-2 focus:border-info",
                value && onReset && "pr-20",
              )}
            >
              {prepareValue ? (
                <span
                  className={classNames(`flex items-center ${disabled && "text-mid-neutral-2"}`)}
                >
                  {valueSelect.label}
                </span>
              ) : (
                <span className='flex items-center text-mid-neutral-2'>{placeholder}</span>
              )}
              <span
                className={`pointer-events-none absolute inset-y-0 right-0 flex items-center pr-5 ${
                  disabled ? "opacity-50" : "opacity-100"
                }`}
              >
                <ArrowDownIcon style={{ fontSize: "20px" }} />
              </span>
              {value && onReset && (
                <div
                  className={`absolute inset-y-0 right-12 flex items-center ${
                    disabled ? "opacity-50" : "opacity-100"
                  }`}
                  onClick={() => {
                    onReset();
                  }}
                >
                  <Cancel2Icon style={{ width: "20px" }} />
                </div>
              )}
            </Listbox.Button>

            <Transition
              show={open}
              as={Fragment}
              leave='transition ease-in duration-100'
              leaveFrom='opacity-100'
              leaveTo='opacity-0'
            >
              <Listbox.Options
                className='absolute z-50 mt-1 max-h-56 w-full overflow-auto bg-white p-2.5 text-base shadow-lg rounded-sm'
                style={{ listStyleType: "none" }}
              >
                {options.map((option, key) => (
                  <Listbox.Option
                    key={key}
                    className={({ active, selected }) =>
                      classNames(
                        active && "bg-light-neutral-4",
                        selected && "bg-light-neutral-4",
                        "relative cursor-pointer p-2.5",
                      )
                    }
                    value={option}
                  >
                    {({ selected }) => (
                      <>
                        <div className='flex items-center'>
                          <span
                            className={classNames(
                              selected ? "font-semibold text-purple" : "font-normal text-deep-blue",
                              "hover:font-semibold block",
                            )}
                          >
                            {option.label}
                          </span>
                        </div>
                      </>
                    )}
                  </Listbox.Option>
                ))}
              </Listbox.Options>
            </Transition>
          </div>
          {under && (
            <div className='flex items-center mt-2 text-xs text-mid-neutral-1'>{under}</div>
          )}
        </>
      )}
    </Listbox>
  );
}

export default CaSelect;
